import { reactive, computed } from 'vue'
import { api } from './api'
import { setLocale, type Locale } from '../i18n'

export type Theme = 'light' | 'dark' | 'system'

export interface InfluxUser {
  uuid: string
  email: string
  name: string
  preferences: {
    theme: Theme
    locale: Locale
  }
}

const state = reactive<{
  user: InfluxUser | null
  /** False until the first `/api/auth/me` settles, so nothing renders early. */
  ready: boolean
  /**
   * The theme chosen with NO account signed in.
   *
   * The sign-in screen and the share-code screen both offer a theme control,
   * and until someone signs in there is no `user.preferences` for it to live
   * in. Without this the control wrote `data-theme` to the document and then
   * every render read the preference back off a null user, resolved `system`,
   * and painted the old theme again — the toggle moved and nothing happened.
   *
   * Seeded from the same cache the pre-boot script reads, so the screen opens
   * on the theme the last visit left.
   */
  anonymousTheme: Theme
  /**
   * Whether {@link anonymousTheme} was chosen on THIS screen, rather than
   * restored from the cache.
   *
   * Signing in adopts the account's stored preferences, which is right — they
   * follow the person between machines. But someone who deliberately switched
   * the theme on the sign-in screen a second earlier has stated a preference,
   * and having the account's older value immediately overwrite it reads as the
   * control being broken. A deliberate choice is carried up to the account
   * instead; a merely remembered one yields to it.
   */
  themeChosenAnonymously: boolean
}>({
  user: null,
  ready: false,
  anonymousTheme: cachedTheme(),
  themeChosenAnonymously: false,
})

/** The mode the pre-boot script stored, or `system` when there is none. */
function cachedTheme(): Theme {
  try {
    const stored = localStorage.getItem('tribit-kit:color-mode')
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    // Private window or blocked site data.
  }
  return 'system'
}

/**
 * Applies the account's stored preferences to the running app.
 *
 * This is the half that was missing: both apps stored `theme` and `locale` on
 * the user and neither read them back, taking their values from per-browser
 * storage instead. Local storage cannot describe a person — only a browser —
 * so a language chosen in Studio said nothing here. The account does.
 */
function applyPreferences(user: InfluxUser): void {
  setLocale(user.preferences.locale)
  applyTheme(user.preferences.theme)
}

/**
 * Resolves the preference and stamps the root element.
 *
 * `data-theme` is always a CONCRETE theme, never absent. Removing it for
 * `system` looks equivalent — the kit's tokens do fall back to
 * `prefers-color-scheme` — but it is not: the attribute left behind by the
 * pre-boot script then survives, so an account set to `system` kept whatever
 * the last visit cached, and the attribute disagreed with what was on screen.
 *
 * This mirrors the kit's own `useColorMode`, which resolves `system` itself
 * and always writes the result. Two writers of one attribute have to agree on
 * its vocabulary.
 */
export function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', resolveTheme(theme))

  // The pre-boot script reads this on the next load to paint the right theme
  // before the bundle parses. It stores the MODE, not the resolved theme, so
  // an account on `system` still follows the OS when the OS changes.
  try {
    localStorage.setItem('tribit-kit:color-mode', theme)
  } catch {
    // Private window or blocked site data — the account still carries it.
  }
}

/** The theme actually showing, with `system` resolved. */
/**
 * The theme on screen, whether or not anyone is signed in.
 *
 * Falls back to the anonymous choice rather than to `system`: on the sign-in
 * and share screens that IS the user's stated preference, and reading `system`
 * there is what made those screens' theme control inert.
 */
export const activeTheme = computed<'light' | 'dark'>(() =>
  resolveTheme(state.user?.preferences.theme ?? state.anonymousTheme),
)

/** The chosen MODE (not the resolved theme), for a control that offers all three. */
export const themePreference = computed<Theme>(
  () => state.user?.preferences.theme ?? state.anonymousTheme,
)

async function load(): Promise<void> {
  const { data } = await api.get<{ user: InfluxUser | null }>('/api/auth/me')
  state.user = data?.user ?? null
  if (state.user !== null) applyPreferences(state.user)
  state.ready = true
}

async function login(email: string, password: string): Promise<string | null> {
  const { data, error } = await api.post<{ user: InfluxUser }>('/api/auth/login', { email, password })
  if (error !== null || data === null) return error ?? 'UNKNOWN'

  const chosen = state.themeChosenAnonymously ? state.anonymousTheme : null
  state.themeChosenAnonymously = false
  state.user = data.user
  applyPreferences(data.user)

  // A theme picked on the sign-in screen is a decision, and it survives the
  // sign-in by being written to the account. Without this the account's older
  // value landed a moment later and the screen flipped back, which looks like
  // the toggle failing rather than a preference being restored.
  if (chosen !== null && chosen !== data.user.preferences.theme) {
    await setPreference({ theme: chosen })
  }
  return null
}

async function logout(): Promise<void> {
  await api.post('/api/auth/logout')
  state.user = null
}

/**
 * Writes a preference to the ACCOUNT, then reflects it locally.
 *
 * Optimistic: the control moves at once and the request follows, because a
 * theme toggle that waits on a round trip feels broken. A failed write leaves
 * the two out of step until the next load, which is the right trade for a
 * preference — the alternative is a control that visibly snaps back.
 */
async function setPreference(patch: { theme?: Theme; locale?: Locale }): Promise<void> {
  if (state.user !== null) {
    state.user.preferences = { ...state.user.preferences, ...patch }
  }
  if (patch.theme !== undefined) {
    // Recorded even when signed in, so signing out does not snap the screen
    // back to whatever the previous visitor chose.
    state.anonymousTheme = patch.theme
    if (state.user === null) state.themeChosenAnonymously = true
    applyTheme(patch.theme)
  }
  if (patch.locale !== undefined) setLocale(patch.locale)

  // An anonymous visitor — a share recipient, or anyone on the sign-in screen
  // — has no account to store this on. The local cache is the whole of their
  // preference, and `applyTheme`/`setLocale` have already written it.
  if (state.user === null) return
  await api.patch('/api/user/preferences', patch)
}

export function useAuth() {
  return {
    user: computed(() => state.user),
    themePreference,
    signedIn: computed(() => state.user !== null),
    ready: computed(() => state.ready),
    activeTheme,
    load,
    login,
    logout,
    setPreference,
  }
}
